import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { Wordmark } from './Wordmark';
import { UserMenu } from './UserMenu';
import type { Notification as AppNotification, Profile } from '@modelcast/types';
import { resolveNavLinksForViewer, type NavLink } from './navLinks';
import { authHeaders } from '../shared/authHeaders';
import { useCurrentProfile } from '../shared/CurrentProfileProvider';

interface NavBarProps {
  variant?: 'dark' | 'light';
  links?: NavLink[];
  primaryCta?: {
    label: string;
    onClick: () => void;
  };
  /** Hide the signed-out "Sign in" button (used on the sign-in page itself). */
  hideSignIn?: boolean;
}

export function NavBar({
  variant = 'dark',
  links = [
    { label: 'Drawing Events', href: '/events' },
    { label: 'Artists', href: '/artists' },
    { label: 'Models', href: '/models' },
  ],
  primaryCta,
  hideSignIn = false,
}: NavBarProps) {
  const navigate = useNavigate();
  const { isSignedIn, getToken, signOut } = useAuth();
  const { profile: currentProfile } = useCurrentProfile();
  const [pendingInvitationCount, setPendingInvitationCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [useCompactMenu, setUseCompactMenu] = useState(false);
  const navInnerRef = useRef<HTMLDivElement | null>(null);
  const fullNavMeasureRef = useRef<HTMLDivElement | null>(null);
  const notificationsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSignedIn) return;

    let cancelled = false;

    async function checkUserDashboard() {
      try {
        const token = await getToken();
        const headers = authHeaders(token);
        const [invitationsResponse, notificationsResponse] = await Promise.all([
          fetch('/api/rooms/my-model-invitations', {
            headers,
          }),
          fetch('/api/notifications', {
            headers,
          }),
        ]);

        if (cancelled) return;

        if (invitationsResponse.ok) {
          const data = await invitationsResponse.json();
          setPendingInvitationCount(data.invitations?.length || 0);
        }

        if (notificationsResponse.ok) {
          const data = await notificationsResponse.json();
          setNotifications(data.notifications || []);
        }
      } catch {
        // Fail silently - dashboard will still fetch its source of truth.
      }
    }

    checkUserDashboard();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, getToken]);

  useEffect(() => {
    if (!isSignedIn) {
      setPendingInvitationCount(0);
      setNotifications([]);
      setNotificationsOpen(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (!isSignedIn) return;

    async function refreshInvitations() {
      try {
        const token = await getToken();
        const response = await fetch('/api/rooms/my-model-invitations', {
          headers: authHeaders(token),
        });
        if (response.ok) {
          const data = await response.json();
          setPendingInvitationCount(data.invitations?.length || 0);
        }
      } catch {
        // Fail silently - the dashboard will still show the source of truth.
      }
    }

    window.addEventListener('model-invitations:updated', refreshInvitations);
    return () => window.removeEventListener('model-invitations:updated', refreshInvitations);
  }, [isSignedIn, getToken]);

  useEffect(() => {
    if (!notificationsOpen && !mobileMenuOpen) return;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;

      if (mobileMenuOpen && !navInnerRef.current?.contains(target)) {
        setMobileMenuOpen(false);
      }

      if (notificationsOpen && !notificationsRef.current?.contains(target)) {
        closeNotificationsMenu();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [notificationsOpen, mobileMenuOpen]);

  const isDark = variant === 'dark';

  const handleDefaultCta = () => {
    if (isSignedIn) {
      navigate('/dashboard');
    } else {
      navigate('/sign-in?redirect_url=/create');
    }
  };

  const handleCtaClick = primaryCta?.onClick || handleDefaultCta;
  const defaultLabel = isSignedIn ? 'Dashboard' : 'Host a session';
  const ctaLabel = primaryCta?.label || defaultLabel;
  const canCreateSession = !!isSignedIn && !!currentProfile && (currentProfile.isModel || currentProfile.isHost);
  const createSessionLabel = 'Create session';
  const handleCreateSession = () => navigate('/create');
  const visibleNotifications = notifications.filter((notification) =>
    notification.type === 'new_follower'
      || notification.type === 'event_invite'
      || notification.type === 'guest_blocked'
      || notification.type === 'reference_pack_access_request'
      || notification.type === 'payout_release_ready'
  );
  const unreadNotificationCount = visibleNotifications.filter((notification) => !notification.readAt).length;
  const bellCount = pendingInvitationCount + unreadNotificationCount;
  const navLinks = useMemo(
    () => resolveNavLinksForViewer(links, Boolean(isSignedIn)),
    [isSignedIn, links]
  );

  const removeReadNotifications = () => {
    setNotifications((current) => current.filter((notification) => !notification.readAt));
  };

  const closeNotificationsMenu = () => {
    setNotificationsOpen(false);
    removeReadNotifications();
  };

  useLayoutEffect(() => {
    const navInner = navInnerRef.current;
    const fullNavMeasure = fullNavMeasureRef.current;
    if (!navInner || !fullNavMeasure) return;

    const updateCompactState = () => {
      setUseCompactMenu(fullNavMeasure.scrollWidth > navInner.clientWidth);
    };

    updateCompactState();

    const observer = new ResizeObserver(updateCompactState);
    observer.observe(navInner);
    observer.observe(fullNavMeasure);
    window.addEventListener('resize', updateCompactState);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateCompactState);
    };
  }, [navLinks, isSignedIn, ctaLabel, bellCount, canCreateSession]);

  useEffect(() => {
    if (!useCompactMenu) {
      setMobileMenuOpen(false);
    }
  }, [useCompactMenu]);

  const handleNavigate = (href: string) => {
    setMobileMenuOpen(false);
    closeNotificationsMenu();
    navigate(href);
  };

  const markVisibleNotificationsRead = async () => {
    const unreadNotifications = visibleNotifications.filter((notification) => !notification.readAt);
    if (unreadNotifications.length === 0) return;

    const readAt = new Date().toISOString();
    setNotifications((current) =>
      current.map((notification) =>
        unreadNotifications.some((unread) => unread.id === notification.id)
          ? { ...notification, readAt }
          : notification
      )
    );

    try {
      const token = await getToken();
      await Promise.all(
        unreadNotifications.map((notification) =>
          fetch(`/api/notifications/${notification.id}/read`, {
            method: 'PATCH',
            headers: authHeaders(token),
          })
        )
      );
    } catch {
      // The next notification fetch will restore anything that did not persist.
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isDark
        ? 'nav-on-dark'
        : 'bg-bg/80 backdrop-blur-lg border-b border-line/60'
    }`}>
      <div ref={navInnerRef} className="relative w-full max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center gap-7 h-[72px]">
        <div
          ref={fullNavMeasureRef}
          aria-hidden="true"
          className="fixed pointer-events-none invisible -z-10 left-0 top-0 h-[72px] flex items-center gap-7 px-4 sm:px-6 lg:px-10 whitespace-nowrap"
        >
          <Wordmark variant={isDark ? 'light' : 'default'} />
          <div className="flex gap-7 ml-7">
            {navLinks.map((link) => (
              <span key={link.href} className="text-[13.5px] font-medium">
                {link.label}
              </span>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2.5">
            {isSignedIn ? (
              <>
                <span className="w-8 h-8" />
                {canCreateSession && <span className="btn btn-sm">{createSessionLabel}</span>}
                <span className="btn btn-sm">{ctaLabel}</span>
                <span className="w-8 h-8" />
              </>
            ) : (
              <>
                <span className="btn btn-sm">Sign in</span>
                <span className="btn btn-sm">{ctaLabel}</span>
              </>
            )}
          </div>
        </div>

        <Wordmark variant={isDark ? 'light' : 'default'} />

        {!useCompactMenu && (
          <>
            <div className="flex gap-7 ml-7">
              {navLinks.map((link) => (
                <NavBarLink key={link.href} link={link} isDark={isDark} />
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2.5">
              {isSignedIn ? (
                <>
                  <div ref={notificationsRef} className="relative">
                    <NotificationBell
                      isDark={isDark}
                      bellCount={bellCount}
                      notificationsOpen={notificationsOpen}
                      onToggle={() => {
                        const willOpen = !notificationsOpen;
                        if (willOpen) {
                          setNotificationsOpen(true);
                          void markVisibleNotificationsRead();
                        } else {
                          closeNotificationsMenu();
                        }
                      }}
                    />
                    {notificationsOpen && (
                      <NotificationsMenu
                        notifications={visibleNotifications}
                        pendingInvitationCount={pendingInvitationCount}
                        onNavigate={handleNavigate}
                      />
                    )}
                  </div>
                  {canCreateSession && (
                    <button
                      onClick={handleCreateSession}
                      className={`btn btn-sm ${isDark ? 'btn-invert' : 'btn-primary'}`}
                    >
                      {createSessionLabel}
                    </button>
                  )}
                  <button
                    onClick={handleCtaClick}
                    className={`btn btn-sm ${
                      canCreateSession
                        ? isDark ? 'btn-outline-light' : 'btn-ghost'
                        : isDark ? 'btn-invert' : 'btn-primary'
                    }`}
                  >
                    {ctaLabel}
                  </button>
                  <UserMenu />
                </>
              ) : (
                <>
                  {!hideSignIn && (
                    <button
                      onClick={() => navigate('/sign-in')}
                      className={`btn btn-sm ${isDark ? 'btn-outline-light' : 'btn-ghost'}`}
                    >
                      Sign in
                    </button>
                  )}
                  <button
                    onClick={handleCtaClick}
                    className={`btn btn-sm ${isDark ? 'btn-invert' : 'btn-primary'}`}
                  >
                    {ctaLabel}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {useCompactMenu && (
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-expanded={mobileMenuOpen}
              aria-label="Open navigation menu"
              className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                isDark
                  ? 'text-white/82 hover:text-white hover:bg-white/10'
                  : 'text-text hover:bg-surface'
              }`}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {mobileMenuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <>
                    <path d="M4 7h16" />
                    <path d="M4 12h16" />
                    <path d="M4 17h16" />
                  </>
                )}
              </svg>
              {bellCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-warn text-white text-[10px] leading-[17px] text-center font-semibold">
                  {bellCount > 9 ? '9+' : bellCount}
                </span>
              )}
            </button>
          </div>
        )}

        {useCompactMenu && mobileMenuOpen && (
          <MobileNavMenu
            isDark={isDark}
            links={navLinks}
            isSignedIn={!!isSignedIn}
            ctaLabel={ctaLabel}
            bellCount={bellCount}
            canCreateSession={canCreateSession}
            notifications={visibleNotifications}
            pendingInvitationCount={pendingInvitationCount}
            onNavigate={handleNavigate}
            onCreateSession={() => {
              setMobileMenuOpen(false);
              handleCreateSession();
            }}
            onPrimaryCta={() => {
              setMobileMenuOpen(false);
              handleCtaClick();
            }}
            onSignIn={() => handleNavigate('/sign-in')}
            onSignOut={() => {
              setMobileMenuOpen(false);
              void signOut();
            }}
          />
        )}
      </div>
    </nav>
  );
}

function NavBarLink({ link, isDark }: { link: NavLink; isDark: boolean }) {
  const className = `text-[13.5px] font-medium transition-colors ${
    isDark
      ? `text-white/70 hover:text-white ${link.active ? 'text-white' : ''}`
      : `text-text-dim hover:text-text ${link.active ? 'text-text' : ''}`
  }`;

  return link.href.startsWith('/') ? (
    <Link to={link.href} className={className}>
      {link.label}
    </Link>
  ) : (
    <a href={link.href} className={className}>
      {link.label}
    </a>
  );
}

function NotificationBell({
  isDark,
  bellCount,
  notificationsOpen,
  onToggle,
}: {
  isDark: boolean;
  bellCount: number;
  notificationsOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      aria-expanded={notificationsOpen}
      aria-label={
        bellCount > 0
          ? `${bellCount} notification${bellCount === 1 ? '' : 's'} need your attention`
          : 'Notifications'
      }
      title="Notifications"
      className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
        isDark
          ? 'text-white/70 hover:text-white hover:bg-white/10'
          : 'text-text-dim hover:text-text hover:bg-surface'
      }`}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {bellCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-warn text-white text-[10px] leading-[17px] text-center font-semibold">
          {bellCount > 9 ? '9+' : bellCount}
        </span>
      )}
    </button>
  );
}

function MobileNavMenu({
  isDark,
  links,
  isSignedIn,
  ctaLabel,
  bellCount,
  canCreateSession,
  notifications,
  pendingInvitationCount,
  onNavigate,
  onCreateSession,
  onPrimaryCta,
  onSignIn,
  onSignOut,
}: {
  isDark: boolean;
  links: NavLink[];
  isSignedIn: boolean;
  ctaLabel: string;
  bellCount: number;
  canCreateSession: boolean;
  notifications: AppNotification[];
  pendingInvitationCount: number;
  onNavigate: (href: string) => void;
  onCreateSession: () => void;
  onPrimaryCta: () => void;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className={`absolute left-3 right-3 top-[calc(100%+8px)] rounded-lg border shadow-2xl overflow-hidden ${
      isDark
        ? 'border-white/12 bg-[#17120f]/96 text-white backdrop-blur-xl'
        : 'border-line bg-panel text-text'
    }`}>
      <div className="p-2">
        {links.map((link) => (
          link.href.startsWith('/') ? (
            <button
              key={link.href}
              type="button"
              onClick={() => onNavigate(link.href)}
              className={`w-full h-11 px-3 rounded flex items-center justify-between text-left text-[14px] font-medium transition-colors ${
                isDark ? 'text-white/82 hover:bg-white/10 hover:text-white' : 'text-text-dim hover:bg-bg-secondary hover:text-text'
              }`}
            >
              <span>{link.label}</span>
              {link.active && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
            </button>
          ) : (
            <a
              key={link.href}
              href={link.href}
              className={`h-11 px-3 rounded flex items-center justify-between text-[14px] font-medium transition-colors ${
                isDark ? 'text-white/82 hover:bg-white/10 hover:text-white' : 'text-text-dim hover:bg-bg-secondary hover:text-text'
              }`}
            >
              <span>{link.label}</span>
              {link.active && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
            </a>
          )
        ))}
      </div>

      {isSignedIn && (bellCount > 0 || notifications.length > 0) && (
        <MobileNotificationsSection
          notifications={notifications}
          pendingInvitationCount={pendingInvitationCount}
          onNavigate={onNavigate}
          isDark={isDark}
        />
      )}

      <div className={`p-3 border-t ${isDark ? 'border-white/10' : 'border-line-soft'}`}>
        {isSignedIn ? (
          <div className="grid grid-cols-2 gap-2">
            {canCreateSession && (
              <button
                type="button"
                onClick={onCreateSession}
                className={`btn btn-sm ${isDark ? 'btn-invert' : 'btn-primary'}`}
              >
                Create session
              </button>
            )}
            <button
              type="button"
              onClick={onPrimaryCta}
              className={`btn btn-sm ${
                canCreateSession
                  ? isDark ? 'btn-outline-light' : 'btn-ghost'
                  : isDark ? 'btn-invert' : 'btn-primary'
              }`}
            >
              {ctaLabel}
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className={`btn btn-sm ${isDark ? 'btn-outline-light' : 'btn-ghost'}`}
            >
              Sign out
            </button>
            <button
              type="button"
              onClick={() => onNavigate('/profile')}
              className={`btn btn-sm ${isDark ? 'btn-outline-light' : 'btn-ghost'} col-span-2`}
            >
              Edit profile
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onSignIn}
              className={`btn btn-sm ${isDark ? 'btn-outline-light' : 'btn-ghost'}`}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={onPrimaryCta}
              className={`btn btn-sm ${isDark ? 'btn-invert' : 'btn-primary'}`}
            >
              {ctaLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileNotificationsSection({
  notifications,
  pendingInvitationCount,
  onNavigate,
  isDark,
}: {
  notifications: AppNotification[];
  pendingInvitationCount: number;
  onNavigate: (href: string) => void;
  isDark: boolean;
}) {
  return (
    <div className={`border-t ${isDark ? 'border-white/10' : 'border-line-soft'}`}>
      <div className={`px-4 pt-3 pb-1 text-[11px] font-mono uppercase ${isDark ? 'text-white/50' : 'text-text-faint'}`}>
        Notifications
      </div>
      <div className="px-2 pb-2">
        {pendingInvitationCount > 0 && (
          <button
            type="button"
            onClick={() => onNavigate('/dashboard?tab=invitations')}
            className={`w-full rounded px-3 py-2.5 text-left text-[13px] transition-colors ${
              isDark ? 'text-white/82 hover:bg-white/10' : 'text-text-dim hover:bg-bg-secondary'
            }`}
          >
            {pendingInvitationCount} model invitation{pendingInvitationCount === 1 ? '' : 's'}
          </button>
        )}
        {notifications.slice(0, 3).map((notification) => {
          const actor = notification.actorProfile;
          const href = getNotificationHref(notification);

          return (
            <button
              key={notification.id}
              type="button"
              onClick={() => onNavigate(href)}
              className={`w-full rounded px-3 py-2.5 text-left text-[13px] transition-colors ${
                isDark ? 'text-white/82 hover:bg-white/10' : 'text-text-dim hover:bg-bg-secondary'
              }`}
            >
              {actor?.displayName || 'Someone'} {getNotificationMessage(notification)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NotificationsMenu({
  notifications,
  pendingInvitationCount,
  onNavigate,
}: {
  notifications: AppNotification[];
  pendingInvitationCount: number;
  onNavigate: (href: string) => void;
}) {
  const hasItems = pendingInvitationCount > 0 || notifications.length > 0;

  return (
    <div className="absolute right-0 top-[calc(100%+10px)] w-[320px] rounded-lg border border-line bg-panel shadow-xl overflow-hidden z-50">
      <div className="px-4 py-3 border-b border-line-soft">
        <div className="font-mono text-[12px] text-text-faint uppercase font-semibold">
          Notifications
        </div>
      </div>

      {!hasItems ? (
        <div className="px-4 py-5 text-[14px] text-text-dim">
          No notifications yet.
        </div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto">
          {pendingInvitationCount > 0 && (
            <button
              type="button"
              onClick={() => onNavigate('/dashboard?tab=invitations')}
              className="w-full flex items-start gap-3 px-4 py-3 text-left border-b border-line-soft hover:bg-bg-secondary transition-colors"
            >
              <div className="mt-0.5 h-8 w-8 rounded-full bg-accent text-white flex items-center justify-center text-[12px] font-semibold">
                {pendingInvitationCount > 9 ? '9+' : pendingInvitationCount}
              </div>
              <div className="min-w-0">
                <p className="text-[14px] text-text">
                  {pendingInvitationCount} model invitation{pendingInvitationCount === 1 ? '' : 's'} need your response.
                </p>
                <p className="mt-0.5 text-[12px] text-text-dim">Open invitations</p>
              </div>
            </button>
          )}

          {notifications.slice(0, 8).map((notification) => {
            const actor = notification.actorProfile;
            const message = getNotificationMessage(notification);
            const href = getNotificationHref(notification);

            return (
              <button
                key={notification.id}
                type="button"
                onClick={() => onNavigate(href)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-line-soft last:border-b-0 hover:bg-bg-secondary transition-colors ${
                  notification.readAt ? 'bg-panel' : 'bg-bg-secondary/60'
                }`}
              >
                {actor ? (
                  <NotificationAvatar profile={actor} />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-bg-secondary border border-line" />
                )}
                <div className="min-w-0">
                  <p className="text-[14px] text-text">
                    {actor ? <span className="font-medium">{actor.displayName}</span> : notification.type === 'payout_release_ready' ? 'LifeDrawing.live' : 'Someone'} {message}
                  </p>
                  <p className="mt-0.5 text-[12px] text-text-dim">
                    {formatRelativeTime(notification.createdAt)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getNotificationHref(notification: AppNotification) {
  if (notification.type === 'event_invite') return '/dashboard?tab=invitations';
  if (notification.type === 'guest_blocked') return '/dashboard';
  if (notification.type === 'reference_pack_access_request') return '/profile?tab=reference-packs';
  if (notification.type === 'payout_release_ready') return '/dashboard';
  return notification.actorProfile ? profileHref(notification.actorProfile) : '/dashboard';
}

function getNotificationMessage(notification: AppNotification) {
  if (notification.type === 'event_invite') return 'invited you to an event.';
  if (notification.type === 'guest_blocked') return 'blocked a guest from one of your events.';
  if (notification.type === 'reference_pack_access_request') return 'requested a reference pack.';
  if (notification.type === 'payout_release_ready') return 'has event payouts ready for review.';
  return 'followed you.';
}

function NotificationAvatar({ profile }: { profile: Profile }) {
  const photoUrl = profile.photos[0]?.url;

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={profile.displayName}
        className="h-8 w-8 rounded-full object-cover"
      />
    );
  }

  return (
    <div className="h-8 w-8 rounded-full bg-bg-secondary border border-line flex items-center justify-center text-[12px] text-text-dim">
      {profile.displayName.charAt(0).toUpperCase()}
    </div>
  );
}

function profileHref(profile: Profile) {
  return profile.username ? `/u/${profile.username}` : `/u/${profile.id}`;
}

function formatRelativeTime(dateStr: string) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
